<script lang="ts">
  import { trapFocus } from "$lib/actions/trapFocus";
  import { getPrivacyConsentRecord, setPrivacyConsent, type ConsentChoice } from "$lib/privacy-consent";
  import { trackPageView } from "$lib/analytics";

  export let open = false;
  export let onClose: () => void = () => {};

  let analytics: ConsentChoice = "denied";
  let externalMedia: ConsentChoice = "denied";
  let wasOpen = false;

  $: if (open && !wasOpen) {
    const record = getPrivacyConsentRecord();
    analytics = record?.analytics ?? "denied";
    externalMedia = record?.externalMedia ?? "denied";
  }
  $: wasOpen = open;

  const save = (nextAnalytics = analytics, nextExternalMedia = externalMedia) => {
    const previousAnalytics = getPrivacyConsentRecord()?.analytics;
    setPrivacyConsent({ analytics: nextAnalytics, externalMedia: nextExternalMedia });
    if (nextAnalytics === "granted" && previousAnalytics !== "granted") trackPageView();
    onClose();
  };
</script>

{#if open}
  <div
    class="fixed inset-0 z-50 flex items-end justify-center bg-slate-950/60 p-0 sm:items-center sm:p-4"
    role="presentation">
    <section
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-preferences-title"
      tabindex="-1"
      use:trapFocus={{ onEscape: onClose, lockBodyScroll: true }}
      class="max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white text-slate-700 sm:rounded-2xl">
      <div
        class="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-200 bg-white px-5 py-4 sm:px-6">
        <div class="min-w-0">
          <h2 id="privacy-preferences-title" class="text-lg font-semibold text-slate-900">Cookie settings</h2>
          <p class="mt-1 text-sm leading-relaxed">
            Choose which optional features the Coach can use. You can change this at any time.
          </p>
        </div>
        <button
          type="button"
          class="-mr-2 -mt-1 flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 active:bg-slate-200 motion-reduce:transition-none"
          aria-label="Close cookie settings"
          on:click={onClose}>
          <svg aria-hidden="true" class="h-5 w-5" viewBox="0 0 20 20" fill="none">
            <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
      </div>

      <div class="space-y-4 px-5 py-4 sm:px-6 sm:py-5">
        <details class="group rounded-xl bg-slate-50 px-4 py-3" aria-labelledby="essential-storage-title">
          <summary
            class="min-h-11 cursor-pointer list-none rounded-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700">
            <span class="flex min-h-11 items-center justify-between gap-3">
              <span>
                <span id="essential-storage-title" class="block font-semibold text-slate-900">Essential storage</span>
                <span class="block text-sm font-semibold text-slate-700">Always active</span>
              </span>
              <svg
                aria-hidden="true"
                class="h-5 w-5 shrink-0 text-slate-500 transition group-open:rotate-180 motion-reduce:transition-none"
                viewBox="0 0 20 20"
                fill="none">
                <path
                  d="m5 7.5 5 5 5-5"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </span>
          </summary>
          <p class="mt-2 text-sm leading-relaxed">
            Keeps the Coach secure and working, including sign-in, sessions, saved progress, preferences and cookie
            choices.
          </p>
        </details>

        <fieldset class="rounded-xl border border-slate-200">
          <legend class="sr-only">Analytics choice</legend>
          <label class="flex min-h-11 cursor-pointer items-start gap-3 p-4">
            <span class="min-w-0 flex-1">
              <span class="block font-semibold text-slate-900">Analytics</span>
              <span class="mt-1 block text-sm leading-relaxed">
                Helps us understand which pages and features are useful.
              </span>
              <span class="mt-2 block text-sm leading-relaxed">
                We do not send your messages or uploaded content to third party analytics.
              </span>
            </span>
            <input
              type="checkbox"
              class="mt-0.5 h-5 w-5 shrink-0 accent-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
              checked={analytics === "granted"}
              on:change={(event) => (analytics = event.currentTarget.checked ? "granted" : "denied")} />
          </label>
        </fieldset>

        <fieldset class="rounded-xl border border-slate-200">
          <legend class="sr-only">Embedded video choice</legend>
          <label class="flex min-h-11 cursor-pointer items-start gap-3 p-4">
            <span class="min-w-0 flex-1">
              <span class="block font-semibold text-slate-900">Embedded videos</span>
              <span class="mt-1 block text-sm leading-relaxed">
                Allows videos from external providers to play. These providers may collect device and usage information.
              </span>
            </span>
            <input
              type="checkbox"
              class="mt-0.5 h-5 w-5 shrink-0 accent-emerald-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700"
              checked={externalMedia === "granted"}
              on:change={(event) => (externalMedia = event.currentTarget.checked ? "granted" : "denied")} />
          </label>
        </fieldset>
      </div>

      <div
        class="sticky bottom-0 z-10 border-t border-slate-200 bg-white px-5 py-3 sm:flex sm:items-center sm:justify-end sm:gap-2 sm:px-6">
        <div class="grid grid-cols-2 gap-2 sm:contents">
          <button
            type="button"
            class="min-h-11 rounded-full px-3 text-sm font-semibold text-slate-700 underline decoration-slate-300 underline-offset-4 transition hover:bg-slate-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-700 active:bg-slate-100 motion-reduce:transition-none sm:px-4"
            on:click={() => save("denied", "denied")}>Reject all optional</button>
          <button
            type="button"
            class="min-h-11 rounded-full px-3 text-sm font-semibold text-emerald-800 underline decoration-emerald-300 underline-offset-4 transition hover:bg-emerald-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 active:bg-emerald-100 motion-reduce:transition-none sm:px-4"
            on:click={() => save("granted", "granted")}>Accept all optional</button>
        </div>
        <button
          type="button"
          class="mt-2 min-h-11 w-full rounded-full bg-emerald-700 px-5 text-sm font-semibold text-white transition hover:bg-emerald-800 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-emerald-700 active:bg-emerald-900 motion-reduce:transition-none sm:mt-0 sm:w-auto"
          on:click={() => save()}>Save choices</button>
      </div>
    </section>
  </div>
{/if}
